import { useState, useEffect, useRef, useCallback } from "react";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

/**
 * useWebSocket Hook
 * 
 * Connects to multiplayer WebSocket server using path-based authentication.
 * The URL format includes user credentials: ws://host/multiplayer/{userId}/{username}
 * 
 * Prerequisites:
 * - User must be authenticated via REST API (POST /api/auth/login)
 * - Token and user data stored in localStorage
 * - UserContext provides userId and username
 * 
 * @param {string|null} url - WebSocket URL with user credentials in path
 * @returns {WebSocket|null} - Connected WebSocket instance or null
 */
export const useWebSocket = (url) => {
  const [socket, setSocket] = useState(null);
  const socketRef = useRef(null);
  const [isConnected, setIsConnected] = useState(false);
  const heartbeatIntervalRef = useRef(null);

  const connectWebSocket = useCallback(() => {
    // Don't connect if URL is null/undefined (user not authenticated)
    if (!url) {
      console.log('WebSocket: No URL provided, skipping connection. User may not be authenticated.');
      return;
    }

    console.log('WebSocket: Connecting to', url);
    const ws = new WebSocket(url);

    ws.onopen = () => {
      console.info("WebSocket connected successfully");
      setSocket(ws);
      socketRef.current = ws;
      setIsConnected(true);

      // Send heartbeat every 30 seconds to keep connection alive
      heartbeatIntervalRef.current = setInterval(() => {
        if (ws.readyState === WebSocket.OPEN) {
          ws.send(JSON.stringify({ type: 'ping' }));
        }
      }, 30000);
    };

    ws.onclose = (event) => {
      console.info("WebSocket disconnected", event.code, event.reason);
      setSocket(null);
      socketRef.current = null;
      setIsConnected(false);

      // Clear heartbeat interval
      if (heartbeatIntervalRef.current) {
        clearInterval(heartbeatIntervalRef.current);
        heartbeatIntervalRef.current = null;
      }

      // Only show toast if it was a clean close (not during component unmount)
      if (event.code !== 1000 && event.code !== 1001) {
        toast.error("Disconnected from multiplayer server");
      }
    };

    ws.onerror = (error) => {
      console.error("WebSocket error:", error);
      toast.error("Failed to connect to multiplayer server. Please check your connection.");
    };

    ws.onmessage = (message) => {
      console.log("WebSocket message received:", message);
    };
  }, [url]);

  useEffect(() => {
    // Close existing connection before creating new one
    if (socketRef.current) {
      socketRef.current.close();
      socketRef.current = null;
    }
    if (heartbeatIntervalRef.current) {
      clearInterval(heartbeatIntervalRef.current);
      heartbeatIntervalRef.current = null;
    }

    connectWebSocket();

    // Only cleanup when component actually unmounts or URL changes
    // Don't close connection on tab visibility changes
    return () => {
      if (heartbeatIntervalRef.current) {
        clearInterval(heartbeatIntervalRef.current);
        heartbeatIntervalRef.current = null;
      }
      if (socketRef.current) {
        console.log('WebSocket: Cleaning up connection on unmount');
        socketRef.current.close();
        socketRef.current = null;
      }
    };
  }, [connectWebSocket]);

  return socket;
};
